<template>
  <div class="image-uploader">
    <b-button-group :size="size">
      <b-button :variant="variant" @click="openCrop">
        <b-icon icon="crop" class="mr-1"></b-icon> {{ label }}
      </b-button>
      <b-button :variant="'outline-' + variant" :disabled="uploading" @click="pickFile"
        v-b-tooltip.hover :title="'Elige o toma una foto; se recorta al centro y se sube directo. Acepta imágenes menores a ' + outputSize + 'px (el recorte no).'">
        <b-spinner v-if="uploading" small class="mr-1"></b-spinner>
        <b-icon v-else icon="camera" class="mr-1"></b-icon> Subir foto
      </b-button>
    </b-button-group>
    <input ref="fileInput" type="file" accept="image/*" class="d-none" @change="onFilePicked" />

    <my-upload field="image" :value="cropVisible" @input="cropVisible = $event" @crop-upload-success="onCropSuccess"
      @crop-upload-fail="onCropFail" lang="es-MX" lang-type="es-MX" :no-circle="true" :width="outputSize"
      :height="outputSize" :url="uploadUrl" :params="{}" :headers="headers" :img-format="cropFormat"></my-upload>
  </div>
</template>

<script>
import axios from "axios";
import myUpload from "vue-image-crop-upload/upload-2.vue";

/**
 * Subida de imagen para catálogos (productos, personalizadores).
 *
 * - "Editar imagen": recorte interactivo (vue-image-crop-upload). En móvil el
 *   componente se reacomoda con los estilos globales de abajo.
 * - "Subir foto": input nativo (cámara o galería). La imagen se recorta al
 *   centro a un cuadrado de `outputSize` px en el navegador y se manda por
 *   axios, así hereda el refresh de token del interceptor y no pesa 5 MB.
 *
 * Ambas rutas envían el mismo multipart (`image`) al mismo `url`.
 */
export default {
  name: "ImageUploader",
  components: { myUpload },
  props: {
    url: { type: String, required: true },
    outputSize: { type: Number, default: 512 },
    label: { type: String, default: "Editar imagen" },
    variant: { type: String, default: "primary" },
    size: { type: String, default: "md" }
  },
  data() {
    return {
      cropVisible: false,
      uploading: false,
      headers: { Authorization: "" }
    };
  },
  computed: {
    // URL absoluta sin barras duplicadas: baseURL termina en "/" y una ruta
    // "/product/1/image" producía ".../tizon//product/1/image", que el
    // backend rechaza. Acepta `url` relativa o absoluta.
    uploadUrl() {
      if (/^https?:\/\//i.test(this.url)) return this.url.replace(/([^:])\/{2,}/g, "$1/");
      const base = (axios.defaults.baseURL || "").replace(/\/+$/, "");
      return base + "/" + this.url.replace(/^\/+/, "");
    },
    // Fotos (JPEG) pesan mucho menos que PNG a 512px o más; los íconos chicos se
    // mantienen en PNG para conservar transparencia.
    cropFormat() {
      return this.outputSize <= 256 ? "png" : "jpg";
    }
  },
  methods: {
    openCrop() {
      // El componente de recorte usa su propio XHR: refrescar el token al abrir.
      this.headers = { Authorization: "Bearer " + (localStorage.getItem("user-token") || "") };
      this.cropVisible = true;
    },
    onCropSuccess(jsonData) {
      this.$emit("uploaded", jsonData);
      this.toast("Éxito", "Imagen actualizada", "success");
    },
    onCropFail(status) {
      this.toast("Error", "No se pudo subir la imagen (" + status + ")", "danger");
    },
    pickFile() {
      this.$refs.fileInput.value = "";
      this.$refs.fileInput.click();
    },
    async onFilePicked(evt) {
      const file = evt.target.files && evt.target.files[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        this.toast("Aviso", "Selecciona una imagen", "warning");
        return;
      }
      this.uploading = true;
      try {
        const blob = await this.squareResize(file, this.outputSize);
        const form = new FormData();
        form.append("image", blob, "photo." + (blob.type === "image/png" ? "png" : "jpg"));
        const r = await axios.post(this.uploadUrl, form, { headers: { "Content-Type": "multipart/form-data" } });
        this.$emit("uploaded", r.data);
        this.toast("Éxito", "Imagen actualizada", "success");
      } catch (err) {
        const msg = (err.response && (err.response.data.message || err.response.data)) || err.message;
        this.toast("Error", "No se pudo subir la imagen: " + msg, "danger");
      } finally {
        this.uploading = false;
      }
    },
    // Recorta al centro (cuadrado) y escala a `size` px usando canvas. Con
    // createImageBitmap + imageOrientation se respeta la rotación EXIF de las
    // fotos de celular; si el navegador no lo soporta, cae a <img>.
    async squareResize(file, size) {
      const source = await this.loadImage(file);
      const w = source.width;
      const h = source.height;
      const side = Math.min(w, h);
      const sx = Math.floor((w - side) / 2);
      const sy = Math.floor((h - side) / 2);
      const target = Math.min(size, side);
      const canvas = document.createElement("canvas");
      canvas.width = target;
      canvas.height = target;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, target, target);
      ctx.drawImage(source, sx, sy, side, side, 0, 0, target, target);
      if (source.close) source.close();
      const type = this.cropFormat === "png" ? "image/png" : "image/jpeg";
      return new Promise((resolve, reject) => {
        canvas.toBlob(b => (b ? resolve(b) : reject(new Error("No se pudo procesar la imagen"))), type, 0.85);
      });
    },
    async loadImage(file) {
      if (window.createImageBitmap) {
        try {
          return await createImageBitmap(file, { imageOrientation: "from-image" });
        } catch (e) {
          // algunos navegadores no aceptan las opciones: seguir con <img>
        }
      }
      return new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(img);
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error("Imagen inválida"));
        };
        img.src = objectUrl;
      });
    },
    toast(title, content, variant) {
      this.$bvToast.toast(content, { title, variant, solid: true, autoHideDelay: 4000, appendToast: true });
    }
  }
};
</script>

<style>
/* vue-image-crop-upload trae un modal fijo de 600x330 sin media queries; en
   pantallas chicas se reacomoda a una columna y cabe en el viewport. */
@media (max-width: 680px) {
  .vue-image-crop-upload .vicp-wrap {
    width: 94vw;
    height: auto;
    max-height: 92vh;
    overflow-y: auto;
    padding: 15px;
  }
  .vue-image-crop-upload .vicp-wrap .vicp-close {
    right: 4px;
    top: 4px;
  }
  .vue-image-crop-upload .vicp-wrap .vicp-close .vicp-icon4::before,
  .vue-image-crop-upload .vicp-wrap .vicp-close .vicp-icon4::after {
    background-color: #555;
  }
  .vue-image-crop-upload .vicp-wrap .vicp-step1 .vicp-drop-area {
    height: auto;
    padding: 25px 10px;
  }
  .vue-image-crop-upload .vicp-wrap .vicp-step2 .vicp-crop .vicp-crop-left,
  .vue-image-crop-upload .vicp-wrap .vicp-step2 .vicp-crop .vicp-crop-right {
    float: none;
    width: 100%;
    margin: 0 auto;
  }
  .vue-image-crop-upload .vicp-wrap .vicp-step2 .vicp-crop .vicp-crop-left .vicp-img-container,
  .vue-image-crop-upload .vicp-wrap .vicp-step2 .vicp-crop .vicp-crop-left .vicp-range {
    margin-left: auto;
    margin-right: auto;
  }
  .vue-image-crop-upload .vicp-wrap .vicp-step2 .vicp-crop .vicp-crop-right {
    margin-top: 15px;
  }
  .vue-image-crop-upload .vicp-wrap .vicp-step2 .vicp-crop .vicp-crop-right .vicp-preview {
    display: flex;
    justify-content: center;
    gap: 10px;
  }
  .vue-image-crop-upload .vicp-wrap .vicp-operate {
    position: static;
    margin-top: 15px;
    text-align: right;
    overflow: hidden;
  }
  .vue-image-crop-upload .vicp-wrap .vicp-operate a {
    float: none;
    display: inline-block;
    margin-left: 8px;
  }
}
</style>
